import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import GradingIcon from '@mui/icons-material/Grading';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { List, ListItem, Popover } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import Modal from './Modal';
import TableWrapper from '../utils/TableWrapper';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import { canTriggerAIGrading, canOverrideGrade } from '../utils/permissionKeys';

function StatusBadge({ status }) {
  const styles = {
    submitted: 'bg-yellow-100 text-yellow-800',
    grading: 'bg-blue-100 text-blue-800',
    graded: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    rework_requested: 'bg-orange-100 text-orange-800',
  };
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${
        styles[status] || 'bg-gray-100 text-gray-800'
      }`}
    >
      {status?.replace('_', ' ') || 'unknown'}
    </span>
  );
}

function gradeColor(grade) {
  if (grade >= 75) return 'text-green-700';
  if (grade >= 60) return 'text-yellow-700';
  return 'text-red-700';
}

function GradeCell({ grade, status }) {
  if (status !== 'graded' || grade === 0)
    return <span className="text-gray-400">-</span>;
  return (
    <span className={`font-semibold ${gradeColor(grade)}`}>{grade}/100</span>
  );
}

function FeedbackModal({ submission, onClose }) {
  if (!submission) return null;
  return (
    <Modal
      modalOpen={!!submission}
      title="Submission Feedback"
      wide
      onClose={onClose}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-lg capitalize">
              {submission.student?.firstname} {submission.student?.lastname}
            </p>
            <p className="text-sm text-gray-500">{submission.student?.email}</p>
          </div>
          <div className="text-right">
            <GradeCell grade={submission.grade} status={submission.status} />
            <p className="text-xs text-gray-500 mt-1">
              Attempt {submission.attempt_number || 1}
            </p>
          </div>
        </div>

        <div className="border-t pt-4">
          <p className="text-sm font-medium text-gray-700 mb-1">Submission</p>
          <a
            href={submission.submission_url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 text-sm break-all"
          >
            {submission.submission_url}
          </a>
        </div>

        {submission.feedback && (
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Feedback</p>
            <div className="bg-gray-50 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap">
              {submission.feedback}
            </div>
          </div>
        )}

        {submission.strengths && (
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Strengths</p>
            <div className="bg-green-50 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap">
              {submission.strengths}
            </div>
          </div>
        )}

        {submission.next_step && (
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Next Step</p>
            <div className="bg-blue-50 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap">
              {submission.next_step}
            </div>
          </div>
        )}

        <StatusBadge status={submission.status} />
      </div>
    </Modal>
  );
}

function OverrideModal({ submission, onClose, onSave }) {
  const [grade, setGrade] = useState(submission?.grade || 0);
  const [feedback, setFeedback] = useState(submission?.feedback || '');

  if (!submission) return null;

  return (
    <Modal modalOpen={!!submission} title="Override Grade" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600 capitalize">
          {submission.student?.firstname} {submission.student?.lastname}
        </p>
        <div>
          <label
            htmlFor="override-grade"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Grade (0-100)
          </label>
          <input
            id="override-grade"
            type="number"
            min={0}
            max={100}
            value={grade}
            onChange={(e) =>
              setGrade(
                Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0))
              )
            }
            className="border border-gray-300 rounded-md px-3 py-2 w-32 focus:outline-none focus:ring-2 focus:ring-claret-500"
          />
        </div>
        <div>
          <label
            htmlFor="override-feedback"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Feedback
          </label>
          <textarea
            id="override-feedback"
            rows={6}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-claret-500"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-gray-300 px-4 py-2 rounded-md text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave({ grade, feedback })}
            className="bg-claret-500 text-white px-4 py-2 rounded-md text-sm"
          >
            Save Override
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function AIGradingPanel({
  assignmentId,
  assignmentTitle,
  grader,
}) {
  const [feedbackSubmission, setFeedbackSubmission] = useState(null);
  const [overrideSubmission, setOverrideSubmission] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const queryClient = useQueryClient();
  const permissions = useSelector((state) => state.auth.permissions);
  const role = useSelector((state) => state.auth.userDetails?.role);
  const allowTrigger = canTriggerAIGrading(permissions, role);
  const allowOverride = canOverrideGrade(permissions, role);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = (event, id) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(id);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };
  // Remote table: MaterialTable drives paging/search and calls this on every
  // page, page-size or search change. Mutations call refreshSubmissions().
  const tableRef = useRef(null);
  const fetchSubmissions = useCallback(
    async (query) => {
      try {
        const res = await axiosInstancev2.get(
          `/student-assignment-submission/filter-by-assignment-id/${assignmentId}`,
          {
            params: {
              email: (query.search || '').trim(),
              $limit: query.pageSize,
              $skip: query.page * query.pageSize,
            },
          }
        );
        const rows = res?.data?.submissions || [];
        return {
          data: rows,
          page: query.page,
          totalCount: res?.data?.total || 0,
        };
      } catch (error) {
        toast.error("Couldn't fetch submissions");
        return { data: [], page: 0, totalCount: 0 };
      }
    },
    [assignmentId]
  );

  const refreshSubmissions = () => {
    tableRef.current?.onQueryChange?.();
    queryClient.invalidateQueries([
      'assignment-submissions-status-stats',
      assignmentId,
    ]);
  };
  // /student-assignment-submission/status-stats/{assignment_id}
  const { data: statusStatsData } = useFetchDatav2(
    ['assignment-submissions-status-stats', assignmentId],
    `/student-assignment-submission/status-stats/${assignmentId}`,
    {},
    "Couldn't fetch status stats",
    !!assignmentId,
    true
  );
  const { average_score, graded, total } = statusStatsData?.data || {};

  const triggerMutation = useMutation(
    () => axiosInstancev2.post(`/grading/trigger/${assignmentId}`),
    {
      onSuccess: (res) => {
        const msg = res?.data?.message || 'Grading triggered';
        toast.success(msg, { duration: 6000 });
        refreshSubmissions();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to trigger grading');
      },
    }
  );

  const regradeMutation = useMutation(
    () => axiosInstancev2.post(`/grading/submission/${openMenu}/retry`),
    {
      onSuccess: (res) => {
        const msg = res?.data?.message || 'Regrade triggered';
        toast.success(msg, { duration: 6000 });
        refreshSubmissions();
      },
    }
  );

  const overrideMutation = useMutation(
    (payload) =>
      axiosInstancev2.put(
        `/grading/submission/${payload.submissionId}/override`,
        { grade: payload.grade, feedback: payload.feedback }
      ),
    {
      onSuccess: () => {
        toast.success('Grade overridden');
        setOverrideSubmission(null);
        refreshSubmissions();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Override failed');
      },
    }
  );

  if (!assignmentId) return null;

  const columns = [
    {
      title: 'Student',
      render: (row) => (
        <div>
          <p className="capitalize font-medium">
            {row.student?.firstname} {row.student?.lastname}
          </p>
          <p className="text-xs text-gray-500">{row.student?.email}</p>
        </div>
      ),
    },
    {
      title: 'Status',
      field: 'status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      title: 'Grade',
      field: 'grade',
      render: (row) => <GradeCell grade={row.grade} status={row.status} />,
    },
    {
      title: 'Attempt',
      field: 'attempt_number',
      render: (row) => (
        <span className="text-sm">{row.attempt_number || 1}</span>
      ),
    },
    {
      title: 'Submission',
      render: (row) =>
        row.submission_url ? (
          <a
            href={row.submission_url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 text-sm"
          >
            View
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: 'Actions',
      render: (row) => (
        <>
          <MoreVert
            fontSize="small"
            onClick={(event) => handleOpenMenu(event, row.id)}
          />
          <Popover
            id={anchorEl ? 'simple-popover' : undefined}
            open={openMenu === row.id}
            onClose={() => handleCloseMenu()}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <List>
              {(row.status === 'graded' || row.status === 'failed') && (
                <ListItem
                  onClick={() => {
                    handleCloseMenu();
                    setFeedbackSubmission(row);
                  }}
                  className="gap-2"
                >
                  <GradingIcon fontSize="inherit" /> Feedback
                </ListItem>
              )}
              {allowOverride && (
                <>
                  {grader === 'ai' && (
                    <ListItem
                      onClick={() => {
                        regradeMutation.mutate(row.id);
                        handleCloseMenu();
                      }}
                      className="gap-2"
                    >
                      <RefreshIcon fontSize="inherit" /> Regrade
                    </ListItem>
                  )}
                  <ListItem
                    onClick={() => {
                      handleCloseMenu();
                      setOverrideSubmission(row);
                    }}
                    className="gap-2"
                  >
                    <EditIcon fontSize="inherit" />{' '}
                    {grader === 'ai' ? 'Override' : 'Grade'}
                  </ListItem>
                </>
              )}
            </List>
          </Popover>
        </>
      ),
    },
  ];

  return (
    <div className="mt-4 space-y-4">
      <div className="bg-white rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg">
              Assignment: <strong>{assignmentTitle}</strong>
            </h3>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={refreshSubmissions}
              className="border border-gray-300 px-3 py-2 rounded-md text-sm flex items-center gap-1"
            >
              <RefreshIcon fontSize="small" /> Refresh
            </button>
            {allowTrigger && grader === 'ai' && (
              <button
                type="button"
                onClick={() => triggerMutation.mutate()}
                disabled={triggerMutation.isLoading || total - graded === 0}
                className="bg-claret-500 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <SmartToyIcon fontSize="small" />
                {triggerMutation.isLoading
                  ? 'Triggering...'
                  : `Grade ${total - graded}`}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">{total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{graded}</p>
            <p className="text-xs text-gray-500">Graded</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-yellow-700">
              {total - graded}
            </p>
            <p className="text-xs text-gray-500">Ungraded</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">
              {average_score > 0 ? `${average_score}%` : '-'}
            </p>
            <p className="text-xs text-gray-500">Avg Score</p>
          </div>
        </div>
      </div>

      <TableWrapper
        title="Submissions"
        columns={columns}
        data={fetchSubmissions}
        tableRef={tableRef}
        options={{ debounceInterval: 500, pageSize: 20 }}
      />

      <FeedbackModal
        submission={feedbackSubmission}
        onClose={() => setFeedbackSubmission(null)}
      />

      <OverrideModal
        submission={overrideSubmission}
        onClose={() => setOverrideSubmission(null)}
        onSave={(payload) =>
          overrideMutation.mutate({
            submissionId: overrideSubmission.id,
            ...payload,
          })
        }
      />
    </div>
  );
}
